import { DataTable } from '@app/components/data-table/data-table';
import { DataTableToolbar } from '@app/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import {
  FilterOptions,
  useServerFilterOptions,
} from '@app/components/data-table/hooks/use-server-filter-options';
import { DataTableProps } from '@app/components/data-table/types/data-table';
import useSimpleDialog from '@app/hooks/use-simple-dialog';
import {
  MobileCombustionActivitiesList,
  PaginatedMobileCombustionActivitiesListList,
} from '@shared/api/types';
import AddDataMenu from '@app/components/imports/scope-1-2-import/components/add-data-menu';
import { baseTableSchema } from '@shared/components/data-table/schema';
import { parseAsInteger, useQueryState } from 'nuqs';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { useCCF } from '../../ccf-context';
import MobileCombustionEditForm from './mobile-combustion-edit-form';
import MobileCombustionPreviewSheet from './mobile-combustion-preview-sheet';
import { useMobileCombustionTableColumns } from './mobile-combustion-table-columns';

export const schema = baseTableSchema.extend({
  uuid: z.string().optional(),
});

interface MobileCombustionTableProps
  extends DataTableProps<PaginatedMobileCombustionActivitiesListList> {}

export default function MobileCombustionTable({
  data,
  isLoading,
  isPreviousData,
  isFetchedAfterMount,
  status,
}: MobileCombustionTableProps) {
  const { scope3Supported } = useCCF();
  const [perPage] = useQueryState('perPage', parseAsInteger.withDefault(10));

  const [selectedActivityUuid, setSelectedActivityUuid] =
    useState<MobileCombustionActivitiesList['uuid']>();

  const {
    showSimpleDialog: showMobileCombustionDialog,
    SimpleDialogComponent: MobileCombustionDialog,
    closeSimpleDialog: closeMobileCombustionDialog,
  } = useSimpleDialog();

  const { results: activities, count } = data || {};
  const pageCount = count ? Math.ceil(count / (perPage || 10)) : 0;

  const { timePeriodsFilter, sitesFilter, fuelsFilter } =
    useServerFilterOptions(
      ['timePeriods', 'sites', 'fuels'] as const,
      data?.filters || {}
    );

  const useFilterOptions: FilterOptions<['timePeriods', 'sites', 'fuels']> =
    useMemo(
      () => ({
        timePeriods: timePeriodsFilter,
        sites: sitesFilter,
        fuels: fuelsFilter,
      }),
      [timePeriodsFilter, sitesFilter, fuelsFilter]
    );

  const { table, context } = useServerDataTable({
    data: activities || [],
    columns: useMobileCombustionTableColumns({
      filters: useFilterOptions,
    }),
    pageCount,
    getRowId: (row) => row.uuid,
    initialState: {
      columnVisibility: {
        uuid: false,
        period: false,
        scope3Emissions: !!scope3Supported,
      },
    },
  });

  return (
    <>
      <DataTable
        table={table}
        context={context}
        className="bg-gray-00"
        rowClassName={() => 'cursor-pointer hover:bg-gray-50'}
        onRowClick={(row) => {
          setSelectedActivityUuid(row?.original.uuid);
          showMobileCombustionDialog();
        }}
        isLoading={(!isFetchedAfterMount && isPreviousData) || isLoading}
        tableLoading={!data && status === 'loading'}
        columnClassName={{
          scope3Emissions: 'border-l',
        }}
      >
        <DataTableToolbar
          table={table}
          filterOrder={['site', 'fuel', 'period']}
        >
          <MobileCombustionPreviewSheet />
          <AddDataMenu
            categorySlug="mobile-combustion"
            onAddManually={() => {
              setSelectedActivityUuid(undefined);
              showMobileCombustionDialog();
            }}
          />
        </DataTableToolbar>
      </DataTable>
      {MobileCombustionDialog({
        header: 'Add mobile combustion data',
        content: (
          <MobileCombustionEditForm
            activityUuid={selectedActivityUuid}
            closeDialog={closeMobileCombustionDialog}
          />
        ),
      })}
    </>
  );
}
